import React, { useEffect, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';
import SVGViewer from './SVGViewer';
import { parseSvgViewBoxFromMarkup } from '../utils/svgViewBox';
import { PUBLIC_BOARD_PREVIEW_MAX_WIDTH } from '../utils/boardPreviewLayout';

const PreviewStage = styled.div`
  position: relative;
  width: 100%;
  max-width: ${props => props.$maxWidth};
  justify-self: center;
  background: linear-gradient(180deg, #ffffff 0%, #f8fbff 100%);
  border-radius: 12px;
  height: ${props => props.$heightPx ? `${props.$heightPx}px` : 'clamp(360px, 60vh, 820px)'};
  padding: 0;
  overflow: hidden;
  border: 1px solid #e2e8f0;

  @media (max-width: 768px) {
    height: ${props => props.$heightPx ? `${props.$heightPx}px` : 'clamp(280px, 52vh, 560px)'};
  }
`;

const StaticImageLayer = styled.div`
  position: absolute;
  inset: 0;
  z-index: 2;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  pointer-events: none;
  opacity: ${props => props.$visible ? 1 : 0.001};
  transition: opacity 0.24s ease;
`;

const StaticImage = styled.img`
  display: block;
  width: 100%;
  height: 100%;
  object-fit: contain;
  object-position: center;
`;

const InteractivePreviewLayer = styled.div`
  position: absolute;
  inset: 0;
  z-index: 1;
`;

const normalizePreviewMaxWidth = (value) => {
  if (typeof value === 'number' && Number.isFinite(value) && value > 0) {
    return `${value}px`;
  }

  const trimmedValue = String(value || '').trim();
  return trimmedValue || '1100px';
};

const MOBILE_BREAKPOINT = 640;
const DEFAULT_STATIC_IMAGE_WIDTH = 1200;
const DEFAULT_STATIC_IMAGE_HEIGHT = 900;

const BoardPublicSvgPreview = ({
  svgContent,
  displayScale = 1,
  pins = [],
  onPinClick,
  maxWidth = PUBLIC_BOARD_PREVIEW_MAX_WIDTH,
  mobileDisplayScaleAdjustment = 1,
  initialZoom = 1,
  minZoom = 0.2,
  maxZoom = 3,
  enablePan = true,
  enableZoom = true,
  enableWheelZoom = false,
  showWheelZoomToggle = true,
  showAlternatePinCalloutColors = true,
  activeFilterGroups = [],
  staticImageSrc = '',
  staticImageAlt = '',
  className,
}) => {
  const [isMobile, setIsMobile] = useState(false);
  const stageRef = useRef(null);
  const staticImageFrameRef = useRef({ firstFrameId: 0, secondFrameId: 0 });
  const [stageWidth, setStageWidth] = useState(0);
  const [showStaticImageOverlay, setShowStaticImageOverlay] = useState(Boolean(String(staticImageSrc || '').trim()));
  const normalizedMaxWidth = useMemo(() => normalizePreviewMaxWidth(maxWidth), [maxWidth]);
  const hasStaticImage = Boolean(String(staticImageSrc || '').trim());
  const hasInteractiveSvg = Boolean(String(svgContent || '').trim());
  const viewBox = useMemo(() => parseSvgViewBoxFromMarkup(svgContent || ''), [svgContent]);
  const aspectRatio = useMemo(() => {
    const viewBoxWidth = Number(viewBox?.width);
    const viewBoxHeight = Number(viewBox?.height);

    if (!Number.isFinite(viewBoxWidth) || viewBoxWidth <= 0 || !Number.isFinite(viewBoxHeight) || viewBoxHeight <= 0) {
      return null;
    }

    return viewBoxWidth / viewBoxHeight;
  }, [viewBox]);
  const staticImageDimensions = useMemo(() => {
    if (!Number.isFinite(aspectRatio) || aspectRatio <= 0) {
      return {
        width: DEFAULT_STATIC_IMAGE_WIDTH,
        height: DEFAULT_STATIC_IMAGE_HEIGHT,
      };
    }

    return {
      width: DEFAULT_STATIC_IMAGE_WIDTH,
      height: Math.max(1, Math.round(DEFAULT_STATIC_IMAGE_WIDTH / aspectRatio)),
    };
  }, [aspectRatio]);

  useEffect(() => {
    const element = stageRef.current;
    if (!element || typeof ResizeObserver === 'undefined') {
      return undefined;
    }

    const updateWidth = (nextWidth) => {
      const roundedWidth = Math.max(0, Math.round(Number(nextWidth) || 0));
      setStageWidth((currentWidth) => (currentWidth === roundedWidth ? currentWidth : roundedWidth));
    };

    updateWidth(element.getBoundingClientRect().width);

    const observer = new ResizeObserver((entries) => {
      const entryWidth = entries?.[0]?.contentRect?.width;
      updateWidth(entryWidth ?? element.getBoundingClientRect().width);
    });

    observer.observe(element);
    return () => observer.disconnect();
  }, [normalizedMaxWidth, svgContent]);

  useEffect(() => {
    const updateIsMobile = () => {
      setIsMobile(window.innerWidth <= MOBILE_BREAKPOINT);
    };

    updateIsMobile();

    window.addEventListener('resize', updateIsMobile);
    return () => window.removeEventListener('resize', updateIsMobile);
  }, []);

  useEffect(() => {
    if (!hasStaticImage) {
      setShowStaticImageOverlay(false);
      return undefined;
    }

    if (!hasInteractiveSvg) {
      setShowStaticImageOverlay(true);
      return undefined;
    }

    setShowStaticImageOverlay(true);

    const frameState = staticImageFrameRef.current;

    frameState.firstFrameId = window.requestAnimationFrame(() => {
      frameState.secondFrameId = window.requestAnimationFrame(() => {
        setShowStaticImageOverlay(false);
      });
    });

    return () => {
      window.cancelAnimationFrame(frameState.firstFrameId);
      window.cancelAnimationFrame(frameState.secondFrameId);
    };
  }, [hasInteractiveSvg, hasStaticImage, staticImageSrc]);

  const normalizedDisplayScale = Number(displayScale);

  const baseScale = Number.isFinite(normalizedDisplayScale) && normalizedDisplayScale > 0
    ? normalizedDisplayScale
    : 1;

  const effectiveScale = isMobile
    ? baseScale * mobileDisplayScaleAdjustment
    : baseScale;

  const stageHeight = useMemo(() => {
    if (!Number.isFinite(aspectRatio) || aspectRatio <= 0 || stageWidth <= 0) {
      return null;
    }

    const isCompactLayout = stageWidth <= 768;
    const isNarrowBoard = aspectRatio < 0.9;
    const isWideBoard = aspectRatio > 1.6;
    const viewerChromeHeight = isCompactLayout ? 50 : 58;
    const contentHeight = stageWidth / aspectRatio;
    const rawHeight = contentHeight + viewerChromeHeight;
    const viewportMaxHeight = typeof window !== 'undefined'
      ? window.innerHeight * (
        isCompactLayout
          ? (isNarrowBoard ? 0.8 : 0.72)
          : (isNarrowBoard ? 0.9 : 0.82)
      )
      : 920;
    const minHeight = isCompactLayout
      ? (isWideBoard ? 210 : 240)
      : (isWideBoard ? 220 : 280);
    const hardMaxHeight = isCompactLayout
      ? (isNarrowBoard ? 760 : isWideBoard ? 500 : 620)
      : (isNarrowBoard ? 1120 : isWideBoard ? 560 : 940);
    const maxHeight = Math.min(hardMaxHeight, viewportMaxHeight);

    return Math.round(Math.max(minHeight, Math.min(maxHeight, rawHeight)));
  }, [aspectRatio, stageWidth]);

  return (
    <PreviewStage
      ref={stageRef}
      className={className}
      $heightPx={stageHeight}
      $maxWidth={normalizedMaxWidth}
    >
      {hasStaticImage && (
        <StaticImageLayer $visible={showStaticImageOverlay || !hasInteractiveSvg}>
          <StaticImage
            src={staticImageSrc}
            alt={staticImageAlt}
            width={staticImageDimensions.width}
            height={staticImageDimensions.height}
            decoding="async"
            loading="eager"
            fetchPriority="high"
          />
        </StaticImageLayer>
      )}
      {hasInteractiveSvg && (
        <InteractivePreviewLayer>
          <SVGViewer
            svgContent={svgContent}
            pins={pins}
            displayScale={effectiveScale}
            onPinClick={onPinClick}
            initialZoom={initialZoom}
            minZoom={minZoom}
            maxZoom={maxZoom}
            enablePan={enablePan}
            enableZoom={enableZoom}
            enableWheelZoom={enableWheelZoom}
            showWheelZoomToggle={showWheelZoomToggle}
            showAlternatePinCalloutColors={showAlternatePinCalloutColors}
            activeFilterGroups={activeFilterGroups}
          />
        </InteractivePreviewLayer>
      )}
    </PreviewStage>
  );
};

export default BoardPublicSvgPreview;
